import { Box, useMediaQuery, useTheme } from '@mui/material';
import MainLayout from 'src/pages/private';
import { useState, FC } from 'react';
import TextField from 'src/components/InputFields/TextField';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox2';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { useFormik } from 'formik';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { voucherTypeInitialValues } from 'src/utils/initialValues.utils';
import {
  apiUrls,
  dialogTitles,
  dialogSubtitles,
} from 'src/utils/properties.utils';
import { voucherTypeValidationSchema } from 'src/utils/validationScheme.utils';
import VoucherTypeGrid from 'src/components/Grids/VoucherTypeGrid';
import TextArea from 'src/components/InputFields/TextArea';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import SearchBar from 'src/components/InputFields/Searchbar';
import {
  PrimaryActionButton,
  ButtonIcon,
  FlexStartContainer,
} from 'src/components/StyledComponents/StyledPageComponents';
import { calculateDialogHeightByErrorCount } from 'src/utils/dialog.utils';

const VoucherTypeManagement: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [voucherTypeAdded, setVoucherTypeAdded] = useState(false);
  const { showSnackbar } = StyledSnackbar();
  const [searchTerm, setSearchTerm] = useState('');

  const formik = useFormik({
    initialValues: voucherTypeInitialValues,
    validationSchema: voucherTypeValidationSchema,
    onSubmit: (values) => handleSubmit(values),
  });

  const getDialogHeight = () =>
    calculateDialogHeightByErrorCount(formik, 680, 25);

  const handleSubmit = async (values: any) => {
    setLoading(true);

    try {
      const addVoucherType = await axiosTokenInstance.post(
        apiUrls.ADD_VOUCHER_TYPE,
        {
          name: values.name,
          displayOrder: values.displayOrder,
          description: values.description,
        }
      );
      showSnackbar(addVoucherType.data.message, 'success');
      setVoucherTypeAdded(!voucherTypeAdded);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  return (
    <MainLayout
      title="Voucher Type Management"
      searchBar={
        <SearchBar
          id="search"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search voucher types by name"
        />
      }
      button={
        <PrimaryActionButton
          className="btn-primary"
          width="200px"
          onClick={handleClickOpen}
        >
          <ButtonIcon component="i" className="bx bx-plus 1" />
          New Voucher Type
        </PrimaryActionButton>
      }
    >
      <VoucherTypeGrid state={voucherTypeAdded} searchTerm={searchTerm} />
      <Box display="flex">
        <Box display="flex" flexDirection="column">
          <CustomDialog
            height={`${getDialogHeight()}px`}
            open={open}
            handleClose={handleClose}
            title={dialogTitles.ADD_VOUCHER_TYPE}
            subtitle={dialogSubtitles.ADD_VOUCHER_TYPE}
            onSubmit={formik.handleSubmit}
            smScreen={smScreen}
            buttonDisabled={loading}
          >
            <TextField
              width="480px"
              id="name"
              label="Voucher Type Name"
              formikProps={formik}
            />

            <TextField
              width="480px"
              id="displayOrder"
              label="Display Order"
              formikProps={formik}
            />

            <FlexStartContainer>
              <TextArea
                id="description"
                label="Description"
                formikProps={formik}
                rows={5}
                height="140px"
              />
            </FlexStartContainer>
          </CustomDialog>
        </Box>
      </Box>
    </MainLayout>
  );
};

export default VoucherTypeManagement;
